import { Paper, TableBody, TableContainer, TableHead, TableRow, Theme, useMediaQuery } from "@mui/material";
import { DefaultButton, SettingTable, settingUseStyles, TableCellButton } from "../Settings";
import SwitchCell from "../components/SwitchCell";
import TextCell from "../components/TextCell";
import { useState } from "react";
import ConfirmationDialog from "../../../components/Misc/Dialog/ConfirmationDialog";
import { useSnackbar } from "notistack";
import SliderRow from "../components/SliderRow";
import { ISliderInterface } from "../components/SliderCell";
import { adjudicationSettings } from "../../../Store/Adjudication/types";
import { ISelectValue } from "../components/SelectCell";
import SelectRow from "../components/SelectRow";

interface IAdjudicationSettings {
    setOption: (key: string, value: string | number | boolean, advanced?: boolean) => void,
    findFVSliderInterface: (name: string, defaultValue?: number) => (ISliderInterface | undefined),
    setFVSliderInterfaceOption: (op: ISliderInterface, basic?: boolean | undefined) => void,
    findDefaultFVSliderInterface: (name: string) => ISliderInterface | undefined,
    getAdjudicationSetting: (setting: adjudicationSettings) => string,
    setAdjudicationSetting: (setting: adjudicationSettings, value: string) => void,
    resetValueToDefault: (name: string) => void,
}

const DEFAULT_LIVENESS_LOWER_VALUE = 70
const DEFAULT_AGE_LOWER_VALUE = 5
const DEFAULT_AGE_UPPER_VALUE = 25
const DEFAULT_ACCEPTED_COUNT = "2"
const DEFAULT_DECISION_COUNT = "3"
const DEFAULT_LIVENESS_THRESHOLD = 50

const AdjudicationSettings: React.FC<IAdjudicationSettings> = (props) => {

    const classes = settingUseStyles();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [openDialog, setOpenDialog] = useState<boolean>(false)
    const [liveAdjudication, setLiveAdjudication] = useState(props.getAdjudicationSetting(adjudicationSettings.LIVE_ADJUDICATION) === "true" ? true : false)
    const [ageActive, setAgeActive] = useState(props.getAdjudicationSetting(adjudicationSettings.FACE_AGE_ENABLE) === "true"? true : false)
    const [livenessActive, setLivenessActive] = useState(props.getAdjudicationSetting(adjudicationSettings.FACE_LIVENESS_ENABLE) === "true"? true : false)
    const { enqueueSnackbar } = useSnackbar();

    const defaultAgeLowerbound: ISliderInterface = {
        key: adjudicationSettings.FACE_AGE_THRESHOLD_LOWERBOUND,
        value: DEFAULT_AGE_LOWER_VALUE
    }

    const defaultAgeUpperbound: ISliderInterface = {
        key: adjudicationSettings.FACE_AGE_THRESHOLD_UPPERBOUND,
        value: DEFAULT_AGE_UPPER_VALUE
    }

    const defaultLivenessLowerbound: ISliderInterface = {
        key: adjudicationSettings.FACE_LIVENESS_RANGE,
        value: DEFAULT_LIVENESS_LOWER_VALUE
    }

    const ageAdjudicationTuple: [ISliderInterface | undefined, ISliderInterface | undefined] = [
        props.findFVSliderInterface(adjudicationSettings.FACE_AGE_THRESHOLD_LOWERBOUND, DEFAULT_AGE_LOWER_VALUE) ?? defaultAgeLowerbound,
        props.findFVSliderInterface(adjudicationSettings.FACE_AGE_THRESHOLD_UPPERBOUND, DEFAULT_AGE_UPPER_VALUE) ?? defaultAgeUpperbound,
    ];

    const livenessAdjudicationTuple: [ISliderInterface | undefined, ISliderInterface | undefined] = [
        props.findFVSliderInterface("LivenessThreshold"),
        props.findFVSliderInterface(adjudicationSettings.FACE_LIVENESS_RANGE, DEFAULT_LIVENESS_LOWER_VALUE) ?? defaultLivenessLowerbound,
    ]

    const defaultAgeAdjudicationTuple: [ISliderInterface | undefined, ISliderInterface | undefined] = [
        defaultAgeLowerbound, defaultAgeUpperbound
    ]

    const defaultLivenessAdjudicationTuple: [ISliderInterface | undefined, ISliderInterface | undefined] = [
        props.findDefaultFVSliderInterface("LivenessThreshold"),
        defaultLivenessLowerbound
    ]

    const handleResetClick = () => {
        handleAgeSwitch(false)
        handleLivenessSwitch(false)
        props.resetValueToDefault("LivenessThreshold")
        props.setFVSliderInterfaceOption(defaultAgeLowerbound, false);
        props.setFVSliderInterfaceOption(defaultAgeUpperbound, false);
        props.setFVSliderInterfaceOption(defaultLivenessLowerbound, false);
        props.setAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_ACCEPTED, DEFAULT_ACCEPTED_COUNT)
        props.setAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_COUNT, DEFAULT_DECISION_COUNT)
    }

    const handleLiveAdjudicationSwitch = (bool: boolean) => {
        setLiveAdjudication(bool)
        props.setAdjudicationSetting(adjudicationSettings.LIVE_ADJUDICATION, bool === true? "true" : "false")
    }

    const handleAgeSwitch = (bool: boolean) => {
        setAgeActive(bool)
        props.setAdjudicationSetting(adjudicationSettings.FACE_AGE_ENABLE, bool === true? "true" : "false")
        const lower = props.findFVSliderInterface(adjudicationSettings.FACE_AGE_THRESHOLD_LOWERBOUND, DEFAULT_AGE_LOWER_VALUE)
        const upper = props.findFVSliderInterface(adjudicationSettings.FACE_AGE_THRESHOLD_UPPERBOUND, DEFAULT_AGE_UPPER_VALUE)
        props.setAdjudicationSetting(adjudicationSettings.FACE_AGE_THRESHOLD_LOWERBOUND, lower!.value.toString())
        props.setAdjudicationSetting(adjudicationSettings.FACE_AGE_THRESHOLD_UPPERBOUND, upper!.value.toString())
        const count = props.getAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_COUNT)
        const accepted = props.getAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_ACCEPTED)
        if (count === "") {
            props.setAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_COUNT, DEFAULT_DECISION_COUNT)
        }
        if (accepted === "") {
            props.setAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_ACCEPTED, DEFAULT_ACCEPTED_COUNT)
        }

        if (bool === false && livenessActive === false) {
            props.setAdjudicationSetting(adjudicationSettings.LIVE_ADJUDICATION, "false")
            setLiveAdjudication(false)
        }
    }

    const handleLivenessSwitch = (bool: boolean) => {
        setLivenessActive(bool)
        props.setAdjudicationSetting(adjudicationSettings.FACE_LIVENESS_ENABLE, bool === true? "true" : "false")
        const liveness = props.findFVSliderInterface("LivenessThreshold")
        props.setFVSliderInterfaceOption(liveness ?? {key: "LivenessThreshold", value: DEFAULT_LIVENESS_THRESHOLD});
        const range = props.findFVSliderInterface(adjudicationSettings.FACE_LIVENESS_RANGE, DEFAULT_LIVENESS_LOWER_VALUE)
        props.setAdjudicationSetting(adjudicationSettings.FACE_LIVENESS_RANGE, range!.value.toString())
        const count = props.getAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_COUNT)
        const accepted = props.getAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_ACCEPTED)
        if (count === "") {
            props.setAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_COUNT, DEFAULT_DECISION_COUNT)
        }
        if (accepted === "") {
            props.setAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_ACCEPTED, DEFAULT_ACCEPTED_COUNT)
        }

        if (bool === false && ageActive === false) {
            props.setAdjudicationSetting(adjudicationSettings.LIVE_ADJUDICATION, "false")
            setLiveAdjudication(false)
        }
    }

    const handleSetAdjudicationCount = (val: string) => {
        props.setAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_COUNT, val)
        const acceptedNeeded = Number(props.getAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_ACCEPTED))
        if (acceptedNeeded > Number(val)) {
            props.setAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_ACCEPTED, val)
        }
    }

    const getAdjudicatorCountEnum = (startValue: number = 1, endValue: number = 9): ISelectValue[] => {
        let array: ISelectValue[] = []
        for (let i = startValue; i <= endValue; i++) {
            array.push({key: i.toString(), value: i.toString()})
        }
        return array
    }

    return (
        <>
        <TableContainer component={Paper} className={classes.container}>
            <SettingTable>
                <TableHead>
                    <TableRow>
                        <TextCell head title="Adjudication" subtitle=""/>
                        <TableCellButton align="right" colSpan={isMobile? 2 : 2}>
                            <DefaultButton variant="text" color="primary" onClick={() => setOpenDialog(true)}>
                                Reset to default
                            </DefaultButton>
                        </TableCellButton>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TextCell title="Live adjudication" subtitle="Enables or disables live adjudication. This is metadata for the web client which indicates whether to wait for the adjudication decision. It also keeps the pending operations available so that the client can ping for the adjudication result."/>
                        <SwitchCell colSpan={isMobile? 2 : 3} state={liveAdjudication} setState={handleLiveAdjudicationSwitch} disabled={!ageActive && !livenessActive}/>
                    </TableRow>
                    <TableRow>
                        <TextCell title="Age adjudication" subtitle="Enables or disables age adjudication."/>
                        <SwitchCell colSpan={isMobile? 2 : 3} state={ageActive} setState={handleAgeSwitch}/>
                    </TableRow>
                    <SliderRow<[ISliderInterface | undefined, ISliderInterface | undefined]>
                        mobile={isMobile}
                        textTitle="Age adjudication range"
                        subtitle="Age range that will trigger adjudication."
                        title="Age range"
                        option={ageAdjudicationTuple}
                        defaultOption={defaultAgeAdjudicationTuple}
                        setOption={(options, basic) => {
                            if (options[0]) {
                                props.setFVSliderInterfaceOption(options[0], basic);
                            }
                            if (options[1]) {
                                props.setFVSliderInterfaceOption(options[1], basic);
                            }
                        }}
                        minValue={0}
                        maxValue={100}
                        type="range"
                        active={ageActive}
                        lockMaxValue
                        valueLabel={(x) => x.toString()}
                    />
                    <TableRow>
                        <TextCell title="Liveness adjudication" subtitle="Enables or disables liveness adjudication."/>
                        <SwitchCell colSpan={isMobile? 2 : 3} state={livenessActive} setState={handleLivenessSwitch}/>
                    </TableRow>
                    <SliderRow<[ISliderInterface | undefined, ISliderInterface | undefined]>
                        mobile={isMobile}
                        textTitle="Liveness adjudication range"
                        subtitle="Liveness range that will trigger adjudication."
                        title="Liveness range"
                        option={livenessAdjudicationTuple}
                        defaultOption={defaultLivenessAdjudicationTuple}
                        setOption={(options, basic) => {
                            if (options[0]) {
                                props.setFVSliderInterfaceOption(options[0], basic);
                            }
                            if (options[1]) {
                                props.setFVSliderInterfaceOption(options[1], basic);
                            }
                        }}
                        minValue={0}
                        maxValue={100}
                        type="range"
                        active={livenessActive}
                        lockMaxValue
                        valueLabel={(x) => x.toString()}
                        triggerOnFirstArrayValueChange={() => enqueueSnackbar("Liveness threshold in liveness tab changed", {variant: "warning"})}
                    />
                    <SelectRow
                        title={"Adjudicator decisions needed"}
                        subtitle="Decisions from adjudicators that are needed for the final decision."
                        label={"Count"}
                        active={ageActive || livenessActive}
                        colSpan={isMobile? 1 : 2}
                        value={props.getAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_COUNT)}
                        isMobile={isMobile}
                        setValue={handleSetAdjudicationCount}
                        getEnumValuesAndKey={getAdjudicatorCountEnum}                        
                    />
                    <SelectRow
                        title={"Accepted decisions needed"}
                        subtitle="Accepted decisions from adjudicators that are required for the case to be accepted."
                        label={"Count"}
                        active={ageActive || livenessActive}
                        colSpan={isMobile? 1 : 2}
                        value={props.getAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_ACCEPTED)}
                        isMobile={isMobile}
                        setValue={(val) => props.setAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_ACCEPTED, val)}
                        getEnumValuesAndKey={() => getAdjudicatorCountEnum(1, Number(props.getAdjudicationSetting(adjudicationSettings.ADJUDICATOR_TOTAL_COUNT)))}                        
                    />
                </TableBody>
            </SettingTable>
        </TableContainer>
        <ConfirmationDialog
            open={openDialog}
            setOpen={setOpenDialog}
            title="Reset adjudication settings"
            text="Reset adjudication settings to default values?"
            cancelText="Cancel"
            confirmText="Yes"
            confirm={handleResetClick}
            confirmColor="error"
        />
        </>
    )
}

export default AdjudicationSettings